import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { supabase } from '../supabaseClient';
import { Button, Card, Input, Label } from './ui/primitives';

const schema = z.object({
  email: z.string().email('Invalid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type FormData = z.infer<typeof schema>;

type AuthMetadata = {
  userType?: unknown;
  companyId?: unknown;
};

const LOGIN_FLOW_MARKER = 'intellerview.login.justSucceeded';

const getErrorDetails = (error: unknown) => {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }

  if (typeof error === 'object' && error !== null) {
    const maybeError = error as { name?: unknown; message?: unknown; stack?: unknown; status?: unknown; code?: unknown };
    return {
      name: typeof maybeError.name === 'string' ? maybeError.name : undefined,
      message: typeof maybeError.message === 'string' ? maybeError.message : String(error),
      stack: typeof maybeError.stack === 'string' ? maybeError.stack : undefined,
      status: maybeError.status,
      code: maybeError.code,
    };
  }

  return {
    message: String(error),
  };
};

const normalizeUserType = (value: unknown): string => {
  return typeof value === 'string' ? value.toUpperCase() : '';
};

const resolveCompanyId = (metadata: AuthMetadata): string | null => {
  return typeof metadata.companyId === 'string' && metadata.companyId.trim().length > 0
    ? metadata.companyId
    : null;
};

const isInvalidCredentialsError = (error: unknown): boolean => {
  const details = getErrorDetails(error);
  const message = (details.message || '').toLowerCase();
  return message.includes('invalid') || details.status === 400 || details.status === 401;
};

const isSessionRaceAbort = (error: unknown): boolean => {
  const details = getErrorDetails(error);
  const message = (details.message || '').toLowerCase();
  return details.name === 'AbortError' || message.includes('signal is aborted') || message.includes('navigator lock');
};

const LoginForm = () => {
  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>({ resolver: zodResolver(schema) });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlType = searchParams.get('type')?.toUpperCase() || 'EMPLOYER';
  const redirectTo = searchParams.get('redirectTo');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const getSafeRedirectPath = () => {
    if (!redirectTo) return null;
    if (!redirectTo.startsWith('/') || redirectTo.startsWith('//')) return null;
    if (redirectTo.startsWith('/login')) return null;
    return redirectTo;
  };

  const onSubmit = async (data: FormData) => {
    if (loading) {
      console.info('[LOGIN_FLOW] Duplicate submit ignored');
      return;
    }

    setErrorMessage('');
    setLoading(true);
    let successfulSession: Awaited<ReturnType<typeof supabase.auth.signInWithPassword>>['data']['session'] = null;
    try {
      localStorage.removeItem('supabase.auth.token');
      sessionStorage.removeItem('supabase.auth.token');
      console.info('[LOGIN_FLOW] signIn start', {
        userType: urlType,
      });

      const { data: authData, error: supabaseError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });

      console.info('[LOGIN_FLOW] signIn result', {
        error: supabaseError ? getErrorDetails(supabaseError) : null,
      });

      if (supabaseError) throw supabaseError;
      if (!authData.session) throw new Error('Login succeeded but Supabase did not return a session.');

      successfulSession = authData.session;
      const metadata = (authData.user?.app_metadata || authData.session.user?.app_metadata || {}) as AuthMetadata;
      const fetchedType = normalizeUserType(metadata.userType);
      const effectiveType = fetchedType || urlType;
      const companyId = resolveCompanyId(metadata);

      console.info('[LOGIN_FLOW] resolved auth context', {
        userType: effectiveType,
        hasCompanyId: Boolean(companyId),
      });

      if (effectiveType === 'EMPLOYER' && !companyId) {
        const setupMessage = 'Your employer account is signed in, but no company is linked yet. Complete company setup before opening the employer dashboard.';
        console.warn('[LOGIN_FLOW] missing employer company metadata', {
          userType: effectiveType,
          hasCompanyId: false,
          targetRoute: '/company-setup',
        });
        sessionStorage.setItem(LOGIN_FLOW_MARKER, '1');
        reset();
        navigate('/company-setup', { state: { setupMessage } });
        return;
      }

      reset();

      const safeRedirectPath = getSafeRedirectPath();
      const targetRoute = safeRedirectPath
        || (effectiveType === 'EMPLOYER'
          ? '/employer/dashboard'
          : effectiveType === 'CANDIDATE' || effectiveType === 'APPLICANT'
            ? '/applicant/dashboard'
            : '/dashboard');

      console.info('[LOGIN_FLOW] target route', {
        targetRoute,
      });
      sessionStorage.setItem(LOGIN_FLOW_MARKER, '1');
      navigate(targetRoute);
    } catch (error: unknown) {
      const details = getErrorDetails(error);
      console.error('[LOGIN_FLOW] caught login error', details);

      if (successfulSession && isSessionRaceAbort(error)) {
        const metadata = (successfulSession.user?.app_metadata || {}) as AuthMetadata;
        const effectiveType = normalizeUserType(metadata.userType) || urlType;
        const targetRoute = effectiveType === 'EMPLOYER' ? '/employer/dashboard' : '/applicant/dashboard';
        console.warn('[LOGIN_FLOW] ignored post-login session race abort', {
          targetRoute,
          error: details,
        });
        sessionStorage.setItem(LOGIN_FLOW_MARKER, '1');
        navigate(targetRoute);
        return;
      }

      if (isInvalidCredentialsError(error)) {
        setErrorMessage('Invalid email or password. Please try again.');
      } else {
        setErrorMessage('An error occurred. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-page flex items-center justify-center px-4 py-10">
      <Card className="w-full max-w-md">
        <h2 className="text-[22px] md:text-page-title font-display font-bold text-txt-primary text-center">
          Log In
        </h2>
        <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4">
          <div>
            <Label htmlFor="login-email">Email</Label>
            <Input
              id="login-email"
              {...register('email')}
              type="email"
              placeholder="you@company.com"
              autoComplete="username"
            />
            {errors.email && <p className="text-danger text-small mt-1">{errors.email.message}</p>}
          </div>
          <div>
            <Label htmlFor="login-password">Password</Label>
            <Input
              id="login-password"
              {...register('password')}
              type="password"
              placeholder="Password"
              autoComplete="current-password"
            />
            {errors.password && <p className="text-danger text-small mt-1">{errors.password.message}</p>}
          </div>
          {errorMessage && <p className="text-danger text-small">{errorMessage}</p>}
          <Button type="submit" disabled={loading} fullWidth>
            {loading ? 'Logging In...' : 'Log In'}
          </Button>
        </form>
        <p className="text-center text-small text-txt-secondary mt-4">
          Don't have an account?{' '}
          <a href={`/signup?type=${urlType.toLowerCase()}`} className="text-brand-600 hover:text-brand-700 font-medium">
            Sign up{urlType ? ` as ${urlType === 'CANDIDATE' ? 'Applicant' : urlType.charAt(0) + urlType.slice(1).toLowerCase()}` : ''}
          </a>
        </p>
      </Card>
    </div>
  );
};

export default LoginForm;
